/*
 Copyright (c) 2013-2016 Chukong Technologies Inc.
 Copyright (c) 2017-2020 Xiamen Yaji Software Co., Ltd.

 http://www.cocos.com

 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated engine source code (the "Software"), a limited,
  worldwide, royalty-free, non-assignable, revocable and non-exclusive license
 to use Cocos Creator solely to develop games on your target platforms. You shall
  not use Cocos Creator software for developing other software or tools that's
  used for developing games. You are not granted to publish, distribute,
  sublicense, and/or sell copies of Cocos Creator.

 The software or tools in this License Agreement are licensed, not sold.
 Xiamen Yaji Software Co., Ltd. reserves all rights not expressly granted to you.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 THE SOFTWARE.
*/

import { ccclass, help, executionOrder, menu, tooltip, displayOrder, type, range, editable, serializable, visible } from 'cc.decorator';
import { BUILD, EDITOR } from 'internal:constants';
import { SpriteAtlas } from '../assets/sprite-atlas';
import { SpriteFrame } from '../assets/sprite-frame';
import { v2, Vec2 } from '../../core/math';
import { ccenum } from '../../core/value-types/enum';
import { clamp } from '../../core/math/utils';
import { IBatcher } from '../renderer/i-batcher';
import { UIRenderer, InstanceMaterialType } from '../framework/ui-renderer';
import { PixelFormat } from '../../core/assets/asset-enum';
import { TextureBase } from '../../core/assets/texture-base';
import { Material, RenderTexture } from '../../core';
import { NodeEventType } from '../../core/scene-graph/node-event';
import { legacyCC } from '../../core/global-exports';
import { PolygonCollider2D } from '../../physics-2d/framework';
import { Button } from '../../ui/button';

/**
 * @en
 * Enum for sprite type.
 *
 * @zh
 * Sprite 类型。
 */
export enum SpriteType {
    /**
     * @en
     * The simple type.
     *
     * @zh
     * 普通类型。
     */
    SIMPLE = 0,
    /**
     * @en
     * The sliced type.
     *
     * @zh
     * 切片（九宫格）类型。
     */
    SLICED = 1,
    /**
     * @en
     * The tiled type.
     *
     * @zh  平铺类型
     */
    TILED =  2,
    /**
     * @en
     * The filled type.
     *
     * @zh
     * 填充类型。
     */
    FILLED = 3,
    /**
     * @en
     * The polygon cutting type.
     *
     * @zh
     * 多邊形裁切类型。
     */
    POLYGONAL = 4,
    // /**
    //  * @en The mesh type.
    //  * @zh  以 Mesh 三角形组成的类型
    //  */
    // MESH: 4
}
ccenum(SpriteType);

/**
 * @en
 * Enum for fill type.
 *
 * @zh
 * 填充类型。
 */
enum FillType {
    /**
     * @en
     * The horizontal fill.
     *
     * @zh
     * 水平方向填充。
     */
    HORIZONTAL = 0,
    /**
     * @en
     * The vertical fill.
     *
     * @zh
     * 垂直方向填充。
     */
    VERTICAL = 1,
    /**
     * @en
     * The radial fill.
     *
     * @zh  径向填充
     */
    RADIAL = 2,
}
ccenum(FillType);

/**
 * @en
 * Sprite Size can track trimmed size, raw size or none.
 *
 * @zh
 * 精灵尺寸调整模式。
 */
enum SizeMode {
    /**
     * @en
     * Use the customized node size.
     *
     * @zh
     * 使用节点预设的尺寸。
     */
    CUSTOM = 0,
    /**
     * @en
     * Match the trimmed size of the sprite frame automatically.
     *
     * @zh
     * 自动适配为精灵裁剪后的尺寸。
     */
    TRIMMED = 1,
    /**
     * @en
     * Match the raw size of the sprite frame automatically.
     *
     * @zh
     * 自动适配为精灵原图尺寸。
     */
    RAW = 2,
}
ccenum(SizeMode);

enum EventType {
    SPRITE_FRAME_CHANGED = 'spriteframe-changed',
}

/**
 * @en
 * Renders a sprite in the scene.
 *
 * @zh
 * 渲染精灵组件。
 */
@ccclass('cc.Sprite')
@help('i18n:cc.Sprite')
@executionOrder(110)
@menu('2D/Sprite')
export class Sprite extends UIRenderer {
    /**
     * @en
     * The sprite atlas where the sprite is.
     *
     * @zh
     * 精灵的图集。
     */
    @type(SpriteAtlas)
    @displayOrder(4)
    @tooltip('i18n:sprite.atlas')
    get spriteAtlas () {
        return this._atlas;
    }
    set spriteAtlas (value) {
        if (this._atlas === value) {
            return;
        }
        this._atlas = value;
    }

    /**
     * @en
     * The sprite frame of the sprite.
     *
     * @zh
     * 精灵的精灵帧。
     */
    @type(SpriteFrame)
    @displayOrder(5)
    @tooltip('i18n:sprite.sprite_frame')
    get spriteFrame () {
        return this._spriteFrame;
    }
    set spriteFrame (value) {
        if (this._spriteFrame === value) {
            return;
        }

        const lastSprite = this._spriteFrame;
        this._spriteFrame = value;
        this.markForUpdateRenderData();
        this._applySpriteFrame(lastSprite);
        if (EDITOR) {
            this.node.emit(EventType.SPRITE_FRAME_CHANGED, this);
        }
    }

    /**
     * @en
     * The sprite render type.
     *
     * @zh
     * 精灵渲染类型。
     *
     * @example
     * ```ts
     * import { Sprite } from 'cc';
     * sprite.type = Sprite.Type.SIMPLE;
     * ```
     */
    @type(SpriteType)
    @displayOrder(6)
    @tooltip('i18n:sprite.type')
    get type () {
        return this._type;
    }
    set type (value: SpriteType) {
        if (this._type !== value) {
            this._type = value;
            if (this._type === SpriteType.POLYGONAL) {
                (this as any).useMulti = false;
                this.updatePolygon();
            } else {
                (this as any).useMulti = true;
                this._editing = false;
                this._editor?.destroy();
                this._editor = null;
            }
            this._flushAssembler();
        }
    }

    /**
     * @en
     * The fill type, This will only have any effect if the "type" is set to “Sprite.Type.FILLED”.
     *
     * @zh
     * 精灵填充类型，仅渲染类型设置为 Sprite.Type.FILLED 时有效。
     *
     * @example
     * ```ts
     * import { Sprite } from 'cc';
     * sprite.fillType = Sprite.FillType.HORIZONTAL;
     * ```
     */
    @type(FillType)
    @displayOrder(6)
    @tooltip('i18n:sprite.fill_type')
    get fillType () {
        return this._fillType;
    }
    set fillType (value: FillType) {
        if (this._fillType !== value) {
            if (value === FillType.RADIAL || this._fillType === FillType.RADIAL) {
                this.destroyRenderData();
            } else if (this.renderData) {
                this.markForUpdateRenderData(true);
            }
        }

        this._fillType = value;
        this._flushAssembler();
    }

    /**
     * @en
     * The fill Center, This will only have any effect if the "type" is set to “Sprite.Type.FILLED”.
     *
     * @zh
     * 填充中心点，仅渲染类型设置为 Sprite.Type.FILLED 时有效。
     *
     * @example
     * ```ts
     * import { Vec2 } from 'cc';
     * sprite.fillCenter = new Vec2(0, 0);
     * ```
     */
    @displayOrder(6)
    @tooltip('i18n:sprite.fill_center')
    get fillCenter () {
        return this._fillCenter;
    }
    set fillCenter (value) {
        this._fillCenter.x = value.x;
        this._fillCenter.y = value.y;
        if (this._type === SpriteType.FILLED && this.renderData) {
            this.markForUpdateRenderData();
        }
    }

    /**
     * @en
     * The fill Start, This will only have any effect if the "type" is set to “Sprite.Type.FILLED”.
     *
     * @zh
     * 填充起始点，仅渲染类型设置为 Sprite.Type.FILLED 时有效。
     *
     * @example
     * ```ts
     * // -1 To 1 between the numbers
     * sprite.fillStart = 0.5;
     * ```
     */
    @range([0, 1, 0.1])
    @displayOrder(6)
    @tooltip('i18n:sprite.fill_start')
    get fillStart () {
        return this._fillStart;
    }

    set fillStart (value) {
        this._fillStart = clamp(value, 0, 1);
        if (this._type === SpriteType.FILLED && this.renderData) {
            this.markForUpdateRenderData();
            this._updateUVs();
        }
    }

    /**
     * @en
     * The fill Range, This will only have any effect if the "type" is set to “Sprite.Type.FILLED”.
     *
     * @zh
     * 填充范围，仅渲染类型设置为 Sprite.Type.FILLED 时有效。
     *
     * @example
     * ```ts
     * // -1 To 1 between the numbers
     * sprite.fillRange = 1;
     * ```
     */
    @range([-1, 1, 0.1])
    @displayOrder(6)
    @tooltip('i18n:sprite.fill_range')
    get fillRange () {
        return this._fillRange;
    }
    set fillRange (value) {
        // positive: counterclockwise, negative: clockwise
        this._fillRange = clamp(value, -1, 1);
        if (this._type === SpriteType.FILLED && this.renderData) {
            this.markForUpdateRenderData();
            this._updateUVs();
        }
    }
    /**
     * @en
     * specify the frame is trimmed or not.
     *
     * @zh
     * 是否使用裁剪模式。
     *
     * @example
     * ```ts
     * sprite.trim = true;
     * ```
     */
    @visible(function (this: Sprite) {
        return (
            this._type === SpriteType.SIMPLE
            || this._type === SpriteType.POLYGONAL
            || this._type === SpriteType.TILED
        );
    })
    @displayOrder(8)
    @tooltip('i18n:sprite.trim')
    get trim () {
        return this._isTrimmedMode;
    }

    set trim (value) {
        if (this._isTrimmedMode === value) {
            return;
        }

        this._isTrimmedMode = value;
        if (this.editing) {
            this._updateEditorPoint();
        }
        if (
            (this._type === SpriteType.SIMPLE ||
                this._type === SpriteType.POLYGONAL ||
                this._type === SpriteType.TILED) &&
            this.renderData
        ) {
            this.markForUpdateRenderData(true);
        }
    }

    @editable
    @displayOrder(5)
    @tooltip('i18n:sprite.gray_scale')
    get grayscale () {
        return this._useGrayscale;
    }
    set grayscale (value) {
        if (this._useGrayscale === value) {
            return;
        }
        this._useGrayscale = value;
        this.changeMaterialForDefine();
        this.updateMaterial();
    }

    /**
     * @en
     * Specify the size tracing mode.
     *
     * @zh
     * 精灵尺寸调整模式。
     *
     * @example
     * ```ts
     * import { Sprite } from 'cc';
     * sprite.sizeMode = Sprite.SizeMode.CUSTOM;
     * ```
     */
    @type(SizeMode)
    @displayOrder(5)
    @tooltip('i18n:sprite.size_mode')
    get sizeMode () {
        return this._sizeMode;
    }
    set sizeMode (value) {
        if (this._sizeMode === value) {
            return;
        }

        this._sizeMode = value;
        if (value !== SizeMode.CUSTOM) {
            this._applySpriteSize();
        }
    }

    @visible(function (this: Sprite) {
        return this._type === SpriteType.POLYGONAL;
    })
    public get editing () {
        return this._editing;
    }
    public set editing (value: boolean) {
        if (EDITOR && value) {
            this._initEditor();
        } else {
            this._editor?.destroy();
            this._editor = null;
        }
        this._editing = value;
    }

    @type(Vec2)
    @visible(function (this: Sprite) {
        return this._type === SpriteType.POLYGONAL;
    })
    public get polygon () {
        return this._polygon;
    }
    public set polygon (value: Vec2[]) {
        this._polygon = value;
        if (this._type === SpriteType.POLYGONAL) {
            this.updatePolygon();
        }
    }

    public get indicesArr () {
        return this._indicesArr;
    }

    public static FillType = FillType;
    public static Type = SpriteType;
    public static SizeMode = SizeMode;
    public static EventType = EventType;

    @serializable
    protected _spriteFrame: SpriteFrame | null = null;
    @serializable
    protected _type = SpriteType.SIMPLE;
    @serializable
    protected _fillType = FillType.HORIZONTAL;
    @serializable
    protected _sizeMode = SizeMode.TRIMMED;
    @serializable
    protected _fillCenter: Vec2 = new Vec2(0, 0);
    @serializable
    protected _fillStart = 0;
    @serializable
    protected _fillRange = 0;
    @serializable
    protected _isTrimmedMode = true;
    @serializable
    protected _useGrayscale = false;
    @serializable
    protected _atlas: SpriteAtlas | null = null;
    @serializable
    protected _polygon: Vec2[] = []; // 需要注意的是 _polygon 是有序的，且是逆时针方向排列

    private _indicesArr: number[] = [];
    private _editing = false;
    private _editor: PolygonCollider2D | null = null;

    public __preload () {
        this.changeMaterialForDefine();
        super.__preload();

        if (EDITOR) {
            this._resized();
            this.node.on(NodeEventType.SIZE_CHANGED, this._resized, this);

            this.node.on(NodeEventType.SIZE_CHANGED, this._updateEditorPoint, this);
            this.node.on(NodeEventType.ANCHOR_CHANGED, this._updateEditorPoint, this);
            const polygonEditor = this.getComponent(PolygonCollider2D);
            if (polygonEditor && !(this.getComponent('cc.Button') as Button)?.enableShapeCheck) {
                polygonEditor.destroy();
            }
        }
    }

    public onEnable () {
        super.onEnable();

        // Force update uv, material define, active material, etc
        this._activateMaterial();
        const spriteFrame = this._spriteFrame;
        if (spriteFrame) {
            this._updateUVs();
            if (this._type === SpriteType.SLICED) {
                spriteFrame.on(SpriteFrame.EVENT_UV_UPDATED, this._updateUVs, this);
            }
            if (this._type === SpriteType.POLYGONAL) {
                this.updatePolygon();
            }
        }
    }

    public onDisable () {
        super.onDisable();
        if (this._spriteFrame && this._type === SpriteType.SLICED) {
            this._spriteFrame.off(SpriteFrame.EVENT_UV_UPDATED, this._updateUVs, this);
        }
    }

    public onDestroy () {
        if (EDITOR) {
            this.node.off(NodeEventType.SIZE_CHANGED, this._resized, this);
        }
        super.onDestroy();
    }

    /**
     * @en
     * Quickly switch to other sprite frame in the sprite atlas.
     * If there is no atlas, the switch fails.
     *
     * @zh
     * 精灵图集内的精灵替换
     *
     * @returns
     */
    public changeSpriteFrameFromAtlas (name: string) {
        if (!this._atlas) {
            console.warn('SpriteAtlas is null.');
            return;
        }
        const sprite = this._atlas.getSpriteFrame(name);
        this.spriteFrame = sprite;
    }

    public changeMaterialForDefine () {
        let texture;
        const lastInstanceMaterialType = this._instanceMaterialType;
        if (this._spriteFrame) {
            texture = this._spriteFrame.texture;
        }
        let value = false;
        if (texture instanceof TextureBase) {
            const format = texture.getPixelFormat();
            value = (format === PixelFormat.RGBA_ETC1 || format === PixelFormat.RGB_A_PVRTC_4BPPV1 || format === PixelFormat.RGB_A_PVRTC_2BPPV1);
        }

        if (value && this.grayscale) {
            this._instanceMaterialType = InstanceMaterialType.USE_ALPHA_SEPARATED_AND_GRAY;
        } else if (value) {
            this._instanceMaterialType = InstanceMaterialType.USE_ALPHA_SEPARATED;
        } else if (this.grayscale) {
            this._instanceMaterialType = InstanceMaterialType.GRAYSCALE;
        } else {
            this._instanceMaterialType = InstanceMaterialType.ADD_COLOR_AND_TEXTURE;
        }
        if (lastInstanceMaterialType !== this._instanceMaterialType) {
            this.updateMaterial();
        }
    }

    protected _updateBuiltinMaterial () {
        let mat = super._updateBuiltinMaterial();
        if (this.spriteFrame && this.spriteFrame.texture instanceof RenderTexture) {
            const defines = { SAMPLE_FROM_RT: true, ...mat.passes[0].defines };
            const renderMat = new Material();
            renderMat.initialize({
                effectAsset: mat.effectAsset,
                defines,
            });
            mat = renderMat;
        }
        return mat;
    }

    protected _render (render: IBatcher) {
        render.commitComp(this, this.renderData, this._spriteFrame, this._assembler, null);
    }

    protected _canRender () {
        if (!super._canRender()) {
            return false;
        }

        const spriteFrame = this._spriteFrame;
        if (!spriteFrame || !spriteFrame.texture) {
            return false;
        }

        return true;
    }

    protected _flushAssembler () {
        const assembler = Sprite.Assembler.getAssembler(this);

        if (this._assembler !== assembler) {
            this.destroyRenderData();
            this._assembler = assembler;
        }

        if (!this.renderData) {
            if (this._assembler && this._assembler.createData) {
                this._renderData = this._assembler.createData(this);
                this.renderData!.material = this.getRenderMaterial(0);
                this.markForUpdateRenderData();
                if (this.spriteFrame) {
                    this._assembler.updateUVs(this);
                }
                this._updateColor();
            }
        }

        // Only Sliced type need update uv when sprite frame insets changed
        if (this._spriteFrame) {
            if (this._type === SpriteType.SLICED) {
                this._spriteFrame.on(SpriteFrame.EVENT_UV_UPDATED, this._updateUVs, this);
            } else {
                this._spriteFrame.off(SpriteFrame.EVENT_UV_UPDATED, this._updateUVs, this);
            }
        }
    }

    private _applySpriteSize () {
        if (this._spriteFrame) {
            if (BUILD || !this._spriteFrame.isDefault) {
                if (SizeMode.RAW === this._sizeMode) {
                    const size = this._spriteFrame.originalSize;
                    this.node._uiProps.uiTransformComp!.setContentSize(size);
                } else if (SizeMode.TRIMMED === this._sizeMode) {
                    const rect = this._spriteFrame.rect;
                    this.node._uiProps.uiTransformComp!.setContentSize(rect.width, rect.height);
                }
            }
        }
    }

    private _resized () {
        if (!EDITOR) {
            return;
        }

        if (this._spriteFrame) {
            const actualSize = this.node._uiProps.uiTransformComp!.contentSize;
            let expectedW = actualSize.width;
            let expectedH = actualSize.height;
            if (this._sizeMode === SizeMode.RAW) {
                const size = this._spriteFrame.originalSize;
                expectedW = size.width;
                expectedH = size.height;
            } else if (this._sizeMode === SizeMode.TRIMMED) {
                const rect = this._spriteFrame.rect;
                expectedW = rect.width;
                expectedH = rect.height;
            }

            if (expectedW !== actualSize.width || expectedH !== actualSize.height) {
                this._sizeMode = SizeMode.CUSTOM;
            }
        }
    }

    private _activateMaterial () {
        const spriteFrame = this._spriteFrame;
        const material = this.getRenderMaterial(0);
        if (spriteFrame) {
            if (material) {
                this.markForUpdateRenderData();
            }
        }

        if (this.renderData) {
            this.renderData.material = material;
        }
    }

    private _updateUVs () {
        if (this._assembler) {
            this._assembler.updateUVs(this);
        }
    }

    private _applySpriteFrame (oldFrame: SpriteFrame | null) {
        const spriteFrame = this._spriteFrame;

        if (oldFrame && this._type === SpriteType.SLICED) {
            oldFrame.off(SpriteFrame.EVENT_UV_UPDATED, this._updateUVs, this);
        }

        let textureChanged = false;
        if (spriteFrame) {
            if (!oldFrame || oldFrame.texture !== spriteFrame.texture) {
                textureChanged = true;
            }
            if (textureChanged) {
                if (this.renderData) this.renderData.textureDirty = true;
                this.changeMaterialForDefine();
            }
            this._applySpriteSize();
            if (this._type === SpriteType.SLICED) {
                spriteFrame.on(SpriteFrame.EVENT_UV_UPDATED, this._updateUVs, this);
            }
        }
    }

    // #region Polygonal

    /** 重置頂點位置為原圖片大小 */
    protected resetPolygon () {
        if (!this.spriteFrame) {
            return;
        }
        const { width, height } = this.spriteFrame.originalSize;
        const l = -width * 0.5;
        const r = width * 0.5;
        const b = -height * 0.5;
        const t = height * 0.5;

        this._polygon = [v2(l, t), v2(l, b), v2(r, b), v2(r, t)];
        this._indicesArr = splitPolygonByTriangle(this.polygon);
        this._updateEditorPoint();
    }

    /** 更新顶点数据 */
    protected updatePolygon () {
        if (this._polygon.length < 3) {
            this.resetPolygon();
        }
        this._indicesArr = splitPolygonByTriangle(this.polygon);
        this.markForUpdateRenderData();
    }

    private _initEditor () {
        this._editor = this.addComponent(PolygonCollider2D);
        this._editor!.editing = true;
        this._editor!.offset = this._proxyEditorOffset(this._editor!.offset);
        this._updateEditorPoint();
    }

    private _updateEditorPoint () {
        if (!this._editor) {
            return;
        }
        this._editor.points = this._polygon.map((p) => this._convertTrimPointToCenterSpace(p));
        this._editor.points = this._proxyEditorPoints(this._editor.points);
        this._editor.points.forEach((p, i) => {
            this._editor!.points[i] = this._proxyPoint(this._editor!.points[i]);
        });
    }

    private _proxyEditorOffset (offset: Vec2): Vec2 {
        return new Proxy(offset, {
            set: (target, key, value) => {
                if (value) {
                    // 讓Editor不能移動offset
                    value = 0;
                }
                return Reflect.set(target, key, value);
            },
        });
    }

    private _proxyEditorPoints (points: Vec2[]): Vec2[] {
        return new Proxy(points, {
            set: (target, key, value) => {
                if (key === 'length') {
                    this.polygon.length = value;
                    this.updatePolygon();
                }
                if (value instanceof Vec2) {
                    const index = Number(key);
                    const trimPoint = this._convertCenterPointToTrimSpace(value);
                    if (index >= this.polygon.length) {
                        this.polygon.push(trimPoint);
                    } else {
                        this.polygon[index] = trimPoint;
                    }
                    value = this._proxyPoint(value);
                    this.updatePolygon();
                }
                return Reflect.set(target, key, value);
            },
        });
    }

    private _proxyPoint (point: Vec2): Vec2 {
        return new Proxy(point, {
            set: (target, key, value) => {
                this.polygon = this._editor!.points.map((p) => this._convertCenterPointToTrimSpace(p));
                return Reflect.set(target, key, value);
            },
        });
    }

    private _convertTrimPointToCenterSpace (point: Vec2): Vec2 {
        const { spriteFrame } = this;
        if (!spriteFrame) {
            return point;
        }
        const uiTrans = this.node._uiProps.uiTransformComp!;
        const cw = uiTrans.width;
        const ch = uiTrans.height;
        const appX = (uiTrans.anchorX - 0.5) * cw;
        const appY = (uiTrans.anchorY - 0.5) * ch;
        const originSize = spriteFrame.originalSize;
        const ow = originSize.width;
        const oh = originSize.height;
        const scaleX = cw / ow;
        const scaleY = ch / oh;
        const { offset, rect } = spriteFrame;
        const rw = rect.width;
        const rh = rect.height;
        const trimScaleX = rw / ow;
        const trimScaleY = rh / oh;

        const result = v2();
        if (!this.trim) {
            // 計算 sprite 在沒有啟用 trim 時影響的位移和大小
            result.x = point.x * trimScaleX + offset.x;
            result.y = point.y * trimScaleY + offset.y;
        } else {
            result.x = point.x;
            result.y = point.y;
        }
        // 計算 UITransform 中 size 及 anchorPoint 影響的位移和大小
        result.x = result.x * scaleX - appX;
        result.y = result.y * scaleY - appY;

        return result;
    }

    private _convertCenterPointToTrimSpace (point: Vec2): Vec2 {
        const { spriteFrame } = this;
        if (!spriteFrame) {
            return point;
        }
        const uiTrans = this.node._uiProps.uiTransformComp!;
        const cw = uiTrans.width;
        const ch = uiTrans.height;
        const appX = (uiTrans.anchorX - 0.5) * cw;
        const appY = (uiTrans.anchorY - 0.5) * ch;
        const originSize = spriteFrame.originalSize;
        const ow = originSize.width;
        const oh = originSize.height;
        const scaleX = cw / ow;
        const scaleY = ch / oh;
        const { offset, rect } = spriteFrame;
        const rw = rect.width;
        const rh = rect.height;
        const trimScaleX = rw / ow;
        const trimScaleY = rh / oh;
        const trimPoint = v2();

        trimPoint.x = (point.x + appX) / scaleX;
        trimPoint.y = (point.y + appY) / scaleY;
        if (!this.trim) {
            trimPoint.x = (trimPoint.x - offset.x) / trimScaleX;
            trimPoint.y = (trimPoint.y - offset.y) / trimScaleY;
        }

        return trimPoint;
    }
    // #endregion
}

legacyCC.Sprite = Sprite;

/**
 * 用耳切法將Polygon切成多個三角形
 * @reference https://forum.cocos.org/t/topic/103699
 */
function splitPolygonByTriangle (points: Vec2[]): number[] {
    if (points.length <= 3) {
        return [0, 1, 2];
    }
    const pointMap: { [key: string]: number } = {}; // point与idx的映射
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        pointMap[`${p.x}-${p.y}`] = i;
    }
    const getIdx = (p: Vec2) => pointMap[`${p.x}-${p.y}`];
    points = points.concat([]);
    const idxs: number[] = [];
    let index = 0;
    while (points.length > 3) {
        const p1 = points[index % points.length];
        const p2 = points[(index + 1) % points.length];
        const p3 = points[(index + 2) % points.length];
        const splitPoint = (index + 1) % points.length;
        const tempV1 = Vec2.subtract(v2(), p2, p1);
        const tempV2 = Vec2.subtract(v2(), p3, p2);
        if (tempV1.cross(tempV2) < 0) {
            // 是一个凹角, 寻找下一个
            index = (index + 1) % points.length;
            continue;
        }
        let hasPoint = false;
        for (let i = 0; i < points.length; i++) {
            const p = points[i];
            if (p !== p1 && p !== p2 && p !== p3 && isInTriangle(p, p1, p2, p3)) {
                hasPoint = true;
                break;
            }
        }
        if (hasPoint) {
            // 当前三角形包含其他点, 寻找下一个
            index = (index + 1) % points.length;
            continue;
        }
        // 找到了耳朵, 切掉
        idxs.push(getIdx(p1), getIdx(p2), getIdx(p3));
        points.splice(splitPoint, 1);
    }
    points.forEach((p) => {
        idxs.push(getIdx(p));
    });
    return idxs;
}

/**
 * 判斷一個點是否在三角形內
 * @param point
 * @param triA
 * @param triB
 * @param triC
 * @returns 是否在三角形內
 */
function isInTriangle (point: Vec2, triA: Vec2, triB: Vec2, triC: Vec2) {
    const AB = Vec2.subtract(v2(), triB, triA);
    const AC = Vec2.subtract(v2(), triC, triA);
    const BC = Vec2.subtract(v2(), triC, triB);
    const AD = Vec2.subtract(v2(), point, triA);
    const BD = Vec2.subtract(v2(), point, triB);
    return (
        AB.cross(AC) >= 0 !== AB.cross(AD) < 0  // D,C 在AB同同方向
        && AB.cross(AC) >= 0 !== AC.cross(AD) >= 0 // D,B 在AC同同方向
        && BC.cross(AB) > 0 !== BC.cross(BD) >= 0
    ); // D,A 在BC同同方向
}
